import { IconArrowInbox } from 'central-icons/IconArrowInbox';
import { useMemo } from 'react';

import { useUserTrialDetails } from '@revelio/auth';
import { MenuItem } from '@revelio/ui';

import { useDownloadData } from '../../../../shared/hooks/useDownloadPlotData';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import { buildScreenerFilters } from '../../filters/utils/get-screener-filters';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../PageConfigProvider/types';
import type { ScreenerMetricValues } from '../PageConfigProvider/types/metric';

const getPrimaryDimension = (
  revelioPageId: RevelioPageId | undefined
): PrimaryFilterId | null => {
  if (revelioPageId === RevelioPageId.COMPANY_SCREENER) {
    return FilterId.COMPANY;
  }
  if (revelioPageId === RevelioPageId.GEOGRAPHY_SCREENER) {
    return FilterId.GEOGRAPHY;
  }
  if (revelioPageId === RevelioPageId.ROLE_SCREENER) {
    return FilterId.ROLE;
  }
  return null;
};

export const ScreenerDataExportMenuItem = () => {
  const { revelioPageId } = useTabContentConfig();
  const { isTrialUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });
  const effectiveFilters = useEffectiveFilters();

  const primaryDimension = getPrimaryDimension(revelioPageId);

  const serialisedFilters = useMemo(() => {
    if (!isScreenerV2Page(revelioPageId) || !primaryDimension) {
      return null;
    }

    const firstMetric: ScreenerMetricValues = 'screener_headcount';
    const filters = buildScreenerFilters({
      effectiveFilters,
      primaryDimension,
      metric: firstMetric,
      pagination: { offset: 0, limit: 1 },
    });

    const { pagination: _pagination, ...filtersWithoutPagination } = filters;

    if (
      Object.keys(filtersWithoutPagination.primary_selector || {}).length ===
        0 &&
      Object.keys(filtersWithoutPagination.segments || {}).length === 0 &&
      Object.keys(
        filtersWithoutPagination.entity_info_selectors
          ?.company_info_selectors || {}
      ).length === 0
    ) {
      return null;
    }

    return {
      ...filtersWithoutPagination,
      pagination: { page: 1 },
    };
  }, [effectiveFilters, revelioPageId, primaryDimension]);

  const { triggerDownload, isLoading } = useDownloadData({
    serialisedFilters,
    downloadPath: 'api/v2/screener/downloads',
  });

  const isDisabled = isTrialUser || !serialisedFilters || isLoading;

  if (!isScreenerV2Page(revelioPageId)) {
    return null;
  }

  return (
    <MenuItem
      icon={<IconArrowInbox size={16} />}
      onClick={triggerDownload}
      disabled={isDisabled}
    >
      Download Data
    </MenuItem>
  );
};
